import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import {
  ArrowUp,
  Camera,
  ChevronDown,
  ClipboardPaste,
  ExternalLink,
  FileUp,
  Image as ImageIcon,
  Images,
  LogOut,
  Plus,
  SquarePen,
  User,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { initializeAmplify } from './auth/intitalizeCognito';
import { rememberPostAuthPath } from './auth/postAuthRedirect';
import LensClient from './clients/lensClient';
import { lensApiHost } from './config/apiConfig';
import { LENS_MODEL_OPTIONS } from './config/lensConfig';
import { getRegion, getStage } from './helpers/appUtils';
import { groundingImages } from './helpers/lensUtils';
import './Lens.css';

export { LENS_MODEL_OPTIONS } from './config/lensConfig';

const APP_TITLE = 'MMIR Lens';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_EDGE = 1568;
const MAX_QUERY_LENGTH = 2000;
const STORAGE_NOTICE = 'Photos and questions are stored for 90 days for quality review.';
const JPEG_QUALITIES = [0.8, 0.7, 0.6];

const SUGGESTIONS = [
  {
    title: 'Identify this landmark',
    subtitle: 'and tell me its history',
    prompt: 'Identify this landmark and tell me its history.',
  },
  {
    title: 'Where was this photo taken?',
    subtitle: 'find the location',
    prompt: 'Where was this photo taken? Find the location.',
  },
  {
    title: 'Who or what is this?',
    subtitle: 'identify the person or object',
    prompt: 'Who or what is this? Identify the person or object.',
  },
  {
    title: 'What happened here?',
    subtitle: 'find the news story',
    prompt: 'What happened here? Find the news story.',
  },
  {
    title: 'What breed is this?',
    subtitle: 'and how to care for it',
    prompt: 'What breed is this, and how do I care for it?',
  },
];

function decodedDataUrlBytes(dataUrl) {
  const base64 = dataUrl.split(',')[1] || '';
  return Math.floor((base64.length * 3) / 4);
}

function loadBrowserImage(url) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The selected image could not be read.'));
    image.src = url;
  });
}

function canvasDataUrl(image, maxEdge, quality) {
  const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('This browser cannot prepare the image.');
  }
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Re-encodes the selected image before it leaves the browser. Besides bounding
 * its size, drawing fresh pixels strips EXIF/XMP metadata such as GPS location.
 */
export async function prepareImageFile(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Choose an image file.');
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const source = await loadBrowserImage(objectUrl);
    let dataUrl = '';
    for (const quality of JPEG_QUALITIES) {
      dataUrl = canvasDataUrl(source, MAX_IMAGE_EDGE, quality);
      if (decodedDataUrlBytes(dataUrl) <= MAX_IMAGE_BYTES) {
        const thumbnail = canvasDataUrl(source, 720, 0.72);
        return { dataUrl, thumbnail, name: file.name || 'Selected photo' };
      }
    }
    throw new Error('Choose an image smaller than 5 MB after processing.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function answerText(response) {
  const answer = response?.mmlm_answer
    || response?.answer
    || response?.answer_with_vtag
    || response?.left?.answer;
  if (typeof answer === 'string') return answer;
  return answer ? JSON.stringify(answer, null, 2) : '';
}

function LoadingDots() {
  return (
    <div className="lens-assistant-row" aria-label="Thinking" role="status">
      <div className="lens-dots">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function sourceLabel(source) {
  return source.domain || source.title || 'Visual source';
}

function sourceInitials(source) {
  const label = sourceLabel(source).replace(/^www\./, '');
  return label
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'VS';
}

function GroundingPreview({ source, compact = false }) {
  const [failed, setFailed] = useState(false);
  const previewUrl = source.previewUrl;
  return (
    <span className={`lens-grounding-preview ${compact ? 'lens-grounding-preview-compact' : ''}`}>
      {!failed && previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="lens-grounding-fallback" aria-hidden="true">
          {sourceInitials(source)}
        </span>
      )}
    </span>
  );
}

function SourcesTray({ sources, onOpen }) {
  return (
    <button
      className="lens-sources-tray"
      type="button"
      aria-label={`Open ${sources.length} visual sources`}
      onClick={onOpen}
    >
      <span className="lens-source-stack" aria-hidden="true">
        {sources.slice(0, 3).map((source, index) => (
          <GroundingPreview
            source={source}
            compact
            key={`${source.url || source.sourceUrl}-${index}`}
          />
        ))}
      </span>
      <span className="lens-sources-copy">
        <strong>Visual sources</strong>
        <span>{sources.length} grounding {sources.length === 1 ? 'result' : 'results'}</span>
      </span>
      <span className="lens-sources-arrow" aria-hidden="true">↑</span>
    </button>
  );
}

function GroundingSheet({ sources, onClose }) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <div className="lens-grounding-layer">
      <button
        className="lens-modal-scrim"
        type="button"
        aria-label="Close visual sources"
        onClick={onClose}
      />
      <section
        className="lens-grounding-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Visual sources"
      >
        <div className="lens-sheet-grabber" aria-hidden="true" />
        <div className="lens-grounding-header">
          <div>
            <span className="lens-eyebrow">MMIR visual grounding</span>
            <h2>Visual sources</h2>
          </div>
          <button type="button" aria-label="Close visual sources" onClick={onClose}>
            <X size={22} />
          </button>
        </div>
        <div className="lens-grounding-list">
          {sources.map((source, index) => {
            const href = source.sourceUrl || source.url;
            const body = (
              <>
                <GroundingPreview source={source} />
                <span className="lens-grounding-card-copy">
                  <span className="lens-source-rank">Source {index + 1}</span>
                  <strong>{source.title}</strong>
                  <span>{sourceLabel(source)}</span>
                </span>
                {href && <ExternalLink size={18} aria-hidden="true" />}
              </>
            );
            return href ? (
              <a
                className="lens-grounding-card"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                key={`${href}-${index}`}
              >
                {body}
              </a>
            ) : (
              <div className="lens-grounding-card" key={`source-${index}`}>{body}</div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function AssistantTurn({ turn, onRetry, onSignInAgain, onOpenSources }) {
  if (turn.status === 'loading') return <LoadingDots />;
  if (turn.status === 'error') {
    return (
      <div className="lens-assistant-row">
        <div className="lens-assistant-bubble lens-error" role="alert">
          <span>{turn.error}</span>
          <button type="button" onClick={turn.needsReauth ? onSignInAgain : onRetry}>
            {turn.needsReauth ? 'Sign in again' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const images = groundingImages(turn.response);
  const answer = answerText(turn.response);
  return (
    <div className="lens-assistant-row">
      <div className="lens-answer">
        {answer ? <ReactMarkdown>{answer}</ReactMarkdown> : <p>No answer this time. Try asking again.</p>}
      </div>
      {images.length > 0 && (
        <SourcesTray
          sources={images}
          onOpen={() => onOpenSources(images)}
        />
      )}
    </div>
  );
}

export function LensExperience({ alias, client, onSignOut, onSignInAgain }) {
  const [query, setQuery] = useState('');
  const [image, setImage] = useState(null);
  const [lastImage, setLastImage] = useState(null);
  const [turns, setTurns] = useState([]);
  const [hint, setHint] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [model, setModel] = useState('sonnet');
  const [preparing, setPreparing] = useState(false);
  const [activeSources, setActiveSources] = useState(null);
  const [dragging, setDragging] = useState(false);
  const conversation = useRef(null);
  const nextTurnId = useRef(1);
  const loading = turns.some((turn) => turn.status === 'loading');
  const latestTurn = turns.length > 0 ? turns[turns.length - 1] : null;
  const effectiveImage = image || lastImage;
  const canSend = Boolean(
    effectiveImage
    && query.trim()
    && query.trim().length <= MAX_QUERY_LENGTH
    && !loading
    && !preparing,
  );

  useEffect(() => {
    const element = conversation.current;
    if (latestTurn && element && element.scrollHeight > element.clientHeight) {
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    }
  }, [latestTurn?.id, latestTurn?.status]);

  const ingestImage = useCallback(async (file) => {
    if (!file) return;
    setHint('');
    setPreparing(true);
    try {
      setImage(await prepareImageFile(file));
      setSheetOpen(false);
    } catch (error) {
      setHint(error?.message || 'Could not load the image. Try a different photo.');
    } finally {
      setPreparing(false);
    }
  }, []);

  const chooseImage = useCallback((event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    void ingestImage(file);
  }, [ingestImage]);

  const pasteImage = useCallback((event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const item = items.find((candidate) => candidate.type?.startsWith('image/'));
    const files = Array.from(event.clipboardData?.files || []);
    const file = item?.getAsFile?.() || files.find((candidate) => candidate.type?.startsWith('image/'));
    if (!file) return;
    event.preventDefault();
    void ingestImage(file);
  }, [ingestImage]);

  const readClipboardImage = useCallback(async () => {
    setHint('');
    try {
      if (!navigator.clipboard?.read) {
        throw new Error('Clipboard image access is not available in this browser.');
      }
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types?.find((type) => type.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], 'clipboard-image', { type: imageType });
          await ingestImage(file);
          return;
        }
      }
      throw new Error('There is no image on the clipboard.');
    } catch (error) {
      setSheetOpen(false);
      setHint(`${error?.message || 'Could not read the clipboard'} Long-press the composer to paste instead.`);
    }
  }, [ingestImage]);

  const dropImage = useCallback((event) => {
    event.preventDefault();
    setDragging(false);
    const file = Array.from(event.dataTransfer?.files || [])
      .find((candidate) => candidate.type?.startsWith('image/'));
    if (file) void ingestImage(file);
  }, [ingestImage]);

  const updateTurn = useCallback((id, patch) => {
    setTurns((previous) => previous.map((turn) => (turn.id === id ? { ...turn, ...patch } : turn)));
  }, []);

  const runTurn = useCallback(async (id, text, photo, selectedModel) => {
    try {
      const response = await client.ask({
        query: text,
        imageDataUrl: photo.dataUrl,
        model: selectedModel,
      });
      if (response?.statusCode && response.statusCode >= 400) {
        const needsReauth = response.statusCode === 401;
        updateTurn(id, {
          status: 'error',
          needsReauth,
          error: needsReauth
            ? 'Your session has expired. Please sign in again.'
            : response.message || `The service returned an error (${response.statusCode}).`,
        });
        return;
      }
      updateTurn(id, { status: 'answer', response, retryImage: null });
    } catch (error) {
      updateTurn(id, {
        status: 'error',
        error: error?.message || 'Could not reach the service. Check your connection and try again.',
        needsReauth: false,
      });
    }
  }, [client, updateTurn]);

  const submit = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }
    if (!canSend) return;
    const id = nextTurnId.current++;
    const text = query.trim();
    const photo = effectiveImage;
    setTurns((previous) => [
      ...previous,
      {
        id,
        query: text,
        thumbnail: photo.thumbnail,
        status: 'loading',
        response: null,
        error: '',
        needsReauth: false,
        retryImage: photo,
        model,
      },
    ]);
    setLastImage(photo);
    setQuery('');
    setImage(null);
    setHint('');
    void runTurn(id, text, photo, model);
  }, [canSend, effectiveImage, model, query, runTurn]);

  const retry = useCallback((turn) => {
    if (loading || !turn.retryImage) return;
    updateTurn(turn.id, { status: 'loading', error: '', needsReauth: false });
    void runTurn(turn.id, turn.query, turn.retryImage, turn.model);
  }, [loading, runTurn, updateTurn]);

  const newChat = useCallback(() => {
    if (client && typeof client.resetSession === 'function') {
      client.resetSession();
    }
    setTurns([]);
    setQuery('');
    setImage(null);
    setLastImage(null);
    setHint('');
  }, [client]);

  const selectSuggestion = useCallback((suggestion) => {
    setQuery(suggestion.prompt);
    if (!image) setSheetOpen(true);
  }, [image]);

  const empty = turns.length === 0;
  const selectedModel = LENS_MODEL_OPTIONS.find((option) => option.key === model)
    || LENS_MODEL_OPTIONS[0];

  return (
    <main
      className={`lens-app ${dragging ? 'lens-dragging' : ''}`}
      onPaste={pasteImage}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
      }}
      onDrop={dropImage}
    >
      <div className="lens-column">
        <header className="lens-topbar">
          <button
            className="lens-frost lens-round-button"
            type="button"
            aria-label="Account"
            aria-expanded={accountOpen}
            onClick={() => {
              setModelOpen(false);
              setAccountOpen((open) => !open);
            }}
          >
            <User size={20} />
          </button>

          <button
            className="lens-frost lens-title-pill"
            type="button"
            aria-label={`Choose model, current model ${selectedModel.title}`}
            aria-expanded={modelOpen}
            onClick={() => {
              setAccountOpen(false);
              setModelOpen((open) => !open);
            }}
          >
            <strong>{APP_TITLE}</strong>
            <span>{selectedModel.title}</span>
            <ChevronDown size={15} aria-hidden="true" />
          </button>

          <button
            className="lens-frost lens-round-button"
            type="button"
            aria-label="New chat"
            disabled={empty && !query && !image}
            onClick={newChat}
          >
            <SquarePen size={20} />
          </button>

          {accountOpen && (
            <div className="lens-frost lens-account-menu">
              <span>{alias || 'Signed in'}</span>
              <button type="button" onClick={onSignOut}><LogOut size={17} /> Sign out</button>
            </div>
          )}

          {modelOpen && (
            <div className="lens-frost lens-model-menu" role="menu" aria-label="Models">
              {LENS_MODEL_OPTIONS.map((option) => (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={option.key === model}
                  className={option.key === model ? 'lens-model-selected' : ''}
                  key={option.key}
                  onClick={() => {
                    setModel(option.key);
                    setModelOpen(false);
                  }}
                >
                  <span>
                    <strong>{option.title}</strong>
                    <small>{option.subtitle}</small>
                  </span>
                  {option.key === model && <span aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          )}
        </header>

        <section className={`lens-conversation ${empty ? 'lens-empty' : ''}`} ref={conversation}>
          {empty ? (
            <div className="lens-empty-copy">
              <h1>{APP_TITLE}</h1>
              <p>Ask anything about a photo</p>
            </div>
          ) : turns.map((turn) => (
            <article
              className="lens-turn"
              key={turn.id}
            >
              <div className="lens-user-row">
                <img src={turn.thumbnail} alt="Submitted attachment" />
                <div className="lens-user-bubble">{turn.query}</div>
              </div>
              <AssistantTurn
                turn={turn}
                onRetry={() => retry(turn)}
                onSignInAgain={onSignInAgain}
                onOpenSources={setActiveSources}
              />
            </article>
          ))}
        </section>

        <div className="lens-bottom">
          {empty && (
            <div className="lens-suggestions" aria-label="Suggested questions">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  className="lens-suggestion"
                  type="button"
                  key={suggestion.title}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <strong>{suggestion.title}</strong>
                  <span>{suggestion.subtitle}</span>
                </button>
              ))}
            </div>
          )}

          {hint && <div className="lens-hint" role="alert">{hint}</div>}

          {effectiveImage && (
            <div className="lens-attachment">
              <img src={effectiveImage.thumbnail} alt="Selected" />
              <button
                type="button"
                aria-label="Remove selected photo"
                onClick={() => {
                  setImage(null);
                  setLastImage(null);
                }}
              >
                <X size={13} />
              </button>
            </div>
          )}

          <form className="lens-composer" onSubmit={submit}>
            <textarea
              aria-label="Question"
              placeholder="Ask about a photo"
              value={query}
              maxLength={MAX_QUERY_LENGTH}
              rows={1}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  submit(event);
                }
              }}
            />
            <button
              type="button"
              className="lens-icon-button"
              aria-label="Add photo"
              disabled={preparing}
              onClick={() => setSheetOpen(true)}
            >
              <Plus size={25} />
            </button>
            <button
              type="submit"
              className="lens-send"
              aria-label="Send"
              disabled={!canSend}
            >
              <ArrowUp size={22} />
            </button>
          </form>
          <p className="lens-notice">{STORAGE_NOTICE}</p>
        </div>
      </div>

      {dragging && (
        <div className="lens-drop-target" aria-hidden="true">
          <Images size={28} />
          <strong>Drop your photo</strong>
        </div>
      )}

      {sheetOpen && (
        <div className="lens-upload-layer" role="presentation">
          <button className="lens-modal-scrim" type="button" aria-label="Close" onClick={() => setSheetOpen(false)} />
          <div className="lens-upload-sheet" role="dialog" aria-modal="true" aria-label="Add a photo">
            <div className="lens-sheet-grabber" />
            <div className="lens-upload-heading">
              <span className="lens-eyebrow">Smart upload</span>
              <strong>Add a photo</strong>
            </div>
            <label className="lens-upload-action">
              <span className="lens-upload-icon"><Camera size={22} aria-hidden="true" /></span>
              <span><strong>Camera</strong><small>Take a new photo</small></span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                aria-label="Take photo"
                onChange={chooseImage}
              />
            </label>
            <label className="lens-upload-action">
              <span className="lens-upload-icon"><ImageIcon size={22} aria-hidden="true" /></span>
              <span><strong>Photos</strong><small>Choose from your library</small></span>
              <input
                type="file"
                accept="image/*"
                aria-label="Upload from library"
                onChange={chooseImage}
              />
            </label>
            <label className="lens-upload-action">
              <span className="lens-upload-icon"><FileUp size={22} aria-hidden="true" /></span>
              <span><strong>Files</strong><small>JPEG, PNG, or WebP</small></span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                aria-label="Choose from files"
                onChange={chooseImage}
              />
            </label>
            <button type="button" className="lens-upload-action" onClick={readClipboardImage}>
              <span className="lens-upload-icon"><ClipboardPaste size={22} aria-hidden="true" /></span>
              <span><strong>Paste image</strong><small>Use a copied screenshot or photo</small></span>
            </button>
            <button type="button" className="lens-sheet-cancel" onClick={() => setSheetOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {activeSources && (
        <GroundingSheet
          sources={activeSources}
          onClose={() => setActiveSources(null)}
        />
      )}
    </main>
  );
}

export default function Lens() {
  const stage = getStage() || 'DEV';
  const region = getRegion(stage) || 'us-west-2';
  const [authStatus, setAuthStatus] = useState('restoring');
  const [alias, setAlias] = useState('');
  const [authError, setAuthError] = useState('');

  const client = useMemo(() => {
    const host = lensApiHost?.[stage]?.[region];
    return host ? new LensClient(host, Auth) : null;
  }, [region, stage]);

  const restoreSession = useCallback(async () => {
    try {
      await Auth.currentSession();
      const user = await Auth.currentAuthenticatedUser();
      setAlias(user?.attributes?.preferred_username || user?.getUsername?.() || user?.username || '');
      setAuthError('');
      setAuthStatus('signedIn');
    } catch (error) {
      setAuthStatus('signedOut');
    }
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = APP_TITLE;
    initializeAmplify(stage, region);
    const listener = ({ payload: { event } }) => {
      if (event === 'signIn' || event === 'cognitoHostedUI') void restoreSession();
      if (event === 'signOut') {
        setAlias('');
        setAuthStatus('signedOut');
      }
    };
    Hub.listen('auth', listener);
    void restoreSession();
    return () => {
      Hub.remove('auth', listener);
      document.title = previousTitle;
    };
  }, [region, restoreSession, stage]);

  const signIn = useCallback(async () => {
    setAuthError('');
    setAuthStatus('signingIn');
    try {
      rememberPostAuthPath('/lens');
      await Auth.federatedSignIn({ customProvider: 'FederateOIDC' });
    } catch (error) {
      setAuthError('Sign-in could not be started. Please try again.');
      setAuthStatus('signedOut');
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthError('');
    try {
      await Auth.signOut();
    } catch (error) {
      setAuthError('Sign-out could not be completed. Your local session was cleared.');
    } finally {
      setAlias('');
      setAuthStatus('signedOut');
    }
  }, []);

  const signInAgain = useCallback(async () => {
    await signOut();
    await signIn();
  }, [signIn, signOut]);

  if (authStatus === 'restoring') {
    return (
      <main className="lens-app lens-auth-screen" aria-live="polite">
        <div className="lens-spinner" aria-hidden="true" />
        <p>Restoring your session…</p>
      </main>
    );
  }

  if (authStatus !== 'signedIn') {
    return (
      <main className="lens-app lens-auth-screen">
        <div className="lens-auth-copy">
          <h1>{APP_TITLE}</h1>
          <p>Ask anything about a photo.</p>
        </div>
        <button className="lens-auth-button" type="button" disabled={authStatus === 'signingIn'} onClick={signIn}>
          {authStatus === 'signingIn' ? 'Opening Midway…' : 'Continue with Midway'}
        </button>
        {authError && <p className="lens-auth-error" role="alert">{authError}</p>}
      </main>
    );
  }

  if (!client) {
    return (
      <main className="lens-app lens-auth-screen">
        <p role="alert">MMIR Lens is not configured for this environment.</p>
      </main>
    );
  }

  return <LensExperience alias={alias} client={client} onSignOut={signOut} onSignInAgain={signInAgain} />;
}
